import { runPipeline } from "./pipeline.js";
import { searchPlaces } from "./geocode.js";
import { resolveOffset, formatUtcOffset } from "./timezone.js";
import {
  canUsePersonalCharts,
  isConstructionLocked,
  tryTesterUnlock,
  clearTesterSession,
  getTesterSessionEmail,
  getTesterSessionCode,
  hasValidTesterSession,
  isAdminSession,
  getTesterUsesRemaining,
  recordTesterUse,
} from "./access.js";
import { buildInterpretationPayload } from "./interpretation/payload.js";
import { buildOutlineReading } from "./interpretation/outline.js";
import { fetchLlmReading } from "./interpretation/api.js";
import { hashPayload } from "./interpretation/hash.js";
import {
  hasAccessSession,
  getAccessEmail,
  getAccessCode,
  getAccessBalance,
  setAccessSession,
  clearAccessSession,
  buildAccessAuth,
  decrementAccessBalance,
} from "./access-code.js";
import { TESTER_MAX_USES } from "./access-config.js";
import {
  renderReadingDocument,
  renderStreamingText,
  showSampleReading,
  showDynamicReading,
} from "./interpretation/render.js";
import { computeLifeClock } from "./cycles/life-clock.js";
import { renderCycleInsight } from "./cycles/render-card.js";
import { initImageLightbox } from "./image-lightbox.js";



/** Demo default: public birth data for Donald Trump (Queens, NY). */
const DEFAULT_BIRTH = {
  name: "Donald Trump",
  placeLabel: "Queens, New York, United States",
  year: 1946,
  month: 6,
  day: 14,
  hour: 10,
  minute: 54,
  timezone: -4,
  latitude: 40.689167,
  longitude: -73.800278,
  ianaZone: "America/New_York",
  houseSystem: "placidus",
};

/** @type {{ label: string, latitude: number, longitude: number, timezone: string } | null} */
let selectedPlace = null;
let placeTimer = null;
/** @type {AbortController | null} */
let placeAbort = null;
let suppressPlaceSearch = false;

/** @type {import("./celestine-adapter.js").ChartModel | null} */
let lastModel = null;
/** @type {object | null} */
let lastBirth = null;
let showingOutline = false;
let showingDynamic = false;

/** Last generated LLM reading markdown (for the Copy button), plus its name. */
let lastReadingMarkdown = null;
let lastReadingName = null;


function $(id) {
  return document.getElementById(id);
}

/**
 * Contact block: click (or press Enter/Space on) the email card to copy the
 * address to the clipboard. Falls back to a temporary textarea when the async
 * Clipboard API is unavailable (insecure context / older browsers).
 */
function initCopyEmail() {
  const box = $("email-address");
  const confirmation = $("copy-confirmation");
  if (!box) return;

  const emailEl = box.querySelector(".contact-email-text") || box;
  const email = emailEl.textContent.trim();
  let hideTimer = null;

  function showConfirmation() {
    if (!confirmation) return;
    confirmation.hidden = false;
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      confirmation.hidden = true;
      hideTimer = null;
    }, 2000);
  }

  function legacyCopy() {
    const temp = document.createElement("textarea");
    temp.value = email;
    temp.setAttribute("readonly", "");
    temp.style.position = "fixed";
    temp.style.top = "-1000px";
    temp.style.opacity = "0";
    document.body.appendChild(temp);
    temp.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch (err) {
      ok = false;
    }
    document.body.removeChild(temp);
    return ok;
  }

  async function copy() {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(email);
        showConfirmation();
        return;
      }
    } catch (err) {
      console.error("Failed to copy with Clipboard API: ", err);
    }
    if (legacyCopy()) showConfirmation();
  }

  box.addEventListener("click", copy);
  box.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      copy();
    }
  });
}

function init() {
  const y = $("y");
  if (y) y.textContent = String(new Date().getFullYear());

  initCopyEmail();
  initImageLightbox();

  const form = $("chart-form");
  if (!form) return;

  fillForm(DEFAULT_BIRTH);
  bindPlaceSearch();
  bindOffsetRefresh();
  bindTesterUnlock();
  bindPaywall();
  bindInterpretationTools();
  applyAccessState();
  applyPaywallState();
  handleCheckoutReturn();

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    if (!canUsePersonalCharts()) {
      showError(
        $("chart-error"),
        "Personal charts are locked while the site is under construction. Unlock a reading pass below, use the tester unlock (email + invite code), or view the Trump sample."
      );
      const status = $("chart-status");
      if (status) {
        status.hidden = false;
        status.textContent = "Locked — demo only.";
      }
      return;
    }
    calculateFromForm();
  });

  $("chart-reset")?.addEventListener("click", () => {
    fillForm(DEFAULT_BIRTH);
    restoreSampleReading();
    calculateFromForm({ forceDemo: true });
  });


  calculateFromForm({ forceDemo: true });
}

function applyAccessState() {
  const form = $("chart-form");
  const unlocked = canUsePersonalCharts();
  const locked = isConstructionLocked() && !unlocked;

  if (form) {
    form.classList.toggle("is-locked", locked);
    form.setAttribute("aria-disabled", locked ? "true" : "false");
  }

  const controls = form
    ? form.querySelectorAll("input, select, textarea, button[type='submit']")
    : [];
  controls.forEach((el) => {
    if (el.id === "chart-reset") return;
    el.disabled = locked;
  });

  const resetBtn = $("chart-reset");
  if (resetBtn) resetBtn.disabled = false;

  updateAccessUi(locked, unlocked);
}

function updateAccessUi(locked, unlocked) {
  const lockNote = $("chart-lock-note");
  if (lockNote) {
    if (!isConstructionLocked()) {
      lockNote.hidden = true;
    } else if (unlocked) {
      const email = getTesterSessionEmail();
      const hasAccess = hasAccessSession();
      if (isAdminSession()) {
        lockNote.hidden = false;
        lockNote.textContent = email
          ? `Admin access active for ${email}. Unlimited chart tests and readings unlocked on this browser.`
          : "Admin access active. Unlimited personal charts unlocked on this browser.";
        lockNote.classList.add("is-unlocked");
        lockNote.classList.remove("is-locked-msg");
      } else if (hasAccess) {
        // Paid buyers: stay silent here — the full pass details (email, access
        // code, balance) live only in the "Unlock full Growth Astrology
        // readings" paywall card (#paywall-balance) to avoid a duplicate box.
        lockNote.hidden = true;
      } else {
        const remaining = getTesterUsesRemaining();
        lockNote.hidden = false;
        lockNote.textContent = `Tester access active for ${email}. Personal charts unlocked on this browser. ${
          remaining === Infinity
            ? "Unlimited tests."
            : `${remaining} test${remaining === 1 ? "" : "s"} remaining.`
        }`;
        lockNote.classList.add("is-unlocked");
        lockNote.classList.remove("is-locked-msg");
      }
    } else {
      lockNote.hidden = false;
      lockNote.innerHTML =
        'Personal charts are locked. Sample reading for Donald Trump <a href="#interpretation">HERE</a>. Purchase a reading pass below.';
      lockNote.classList.add("is-locked-msg");
      lockNote.classList.remove("is-unlocked");
    }
  }

  const unlockPanel = $("tester-unlock");
  if (unlockPanel) {
    unlockPanel.hidden = !isConstructionLocked();
  }

  const unlockStatus = $("tester-unlock-status");
  if (unlockStatus && unlocked && isConstructionLocked()) {
    unlockStatus.hidden = false;
    unlockStatus.textContent = "Unlocked for testing on this device.";
    unlockStatus.classList.remove("is-error");
    unlockStatus.classList.add("is-ok");
  }

  const lockBtn = $("tester-lock-again");
  if (lockBtn) {
    lockBtn.hidden = !(isConstructionLocked() && hasValidTesterSession());
  }

  updateInterpretationUi();
}

function bindTesterUnlock() {
  const form = $("tester-unlock-form");
  if (!form) return;

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const email = $("tester-email")?.value || "";
    const code = $("tester-code")?.value || "";
    const status = $("tester-unlock-status");
    const result = tryTesterUnlock(email, code);

    if (!result.ok) {
      if (status) {
        status.hidden = false;
        status.textContent = result.error;
        status.classList.add("is-error");
        status.classList.remove("is-ok");
      }
      return;
    }

    if (status) {
      status.hidden = false;
      if (isAdminSession()) {
        status.textContent = `Admin access active for ${result.email}. Unlimited chart tests and readings.`;
      } else {
        const remaining = getTesterUsesRemaining();
        status.textContent = `Unlocked for ${result.email}. You can calculate personal charts (${
          remaining === Infinity ? "unlimited" : remaining + " tests remaining"
        }).`;
      }
      status.classList.remove("is-error");
      status.classList.add("is-ok");
    }
    applyAccessState();
  });

  $("tester-lock-again")?.addEventListener("click", () => {
    clearTesterSession();
    fillForm(DEFAULT_BIRTH);
    applyAccessState();
    restoreSampleReading();
    calculateFromForm({ forceDemo: true });
    const status = $("tester-unlock-status");
    if (status) {
      status.hidden = false;
      status.textContent = "Tester session cleared. Back to demo only.";
      status.classList.remove("is-ok");
      status.classList.remove("is-error");
    }
  });
}

function fillForm(data) {
  suppressPlaceSearch = true;
  setVal("field-name", data.name);
  setVal("field-place", data.placeLabel || "");
  setVal("field-year", data.year);
  setVal("field-month", data.month);
  setVal("field-day", data.day);

  const clock = fromHour24(data.hour);
  setVal("field-hour", clock.hour12);
  setVal("field-minute", data.minute);
  setVal("field-ampm", clock.ampm);

  setVal("field-timezone", data.timezone);
  setVal("field-latitude", data.latitude);
  setVal("field-longitude", data.longitude);
  setVal("field-iana-zone", data.ianaZone || "");

  selectedPlace = {
    label: data.placeLabel || "",
    latitude: data.latitude,
    longitude: data.longitude,
    timezone: data.ianaZone || "",
  };

  hideSuggestions();
  updatePlaceResolved();
  refreshOffsetFromZone();
  suppressPlaceSearch = false;
}

function setVal(id, value) {
  const el = $(id);
  if (el) el.value = value ?? "";
}

function bindPlaceSearch() {
  const input = $("field-place");
  const list = $("place-suggestions");
  if (!input || !list) return;

  input.addEventListener("input", () => {
    if (suppressPlaceSearch) return;
    if (!canUsePersonalCharts()) return;
    selectedPlace = null;
    updatePlaceResolved();
    schedulePlaceSearch(input.value);
  });

  input.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      hideSuggestions();
      return;
    }
    if (e.key === "ArrowDown") {
      const first = list.querySelector('[role="option"]');
      if (first) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  input.addEventListener("blur", () => {
    // Delay so option click can register
    setTimeout(() => {
      if (!list.contains(document.activeElement)) hideSuggestions();
    }, 150);
  });

  document.addEventListener("click", (e) => {
    if (!e.target.closest(".place-field")) hideSuggestions();
  });
}

function bindOffsetRefresh() {
  for (const id of [
    "field-year",
    "field-month",
    "field-day",
    "field-hour",
    "field-minute",
    "field-ampm",
  ]) {
    $(id)?.addEventListener("change", () => refreshOffsetFromZone());
    $(id)?.addEventListener("input", () => {
      // light debounce via rAF not needed; cheap
      refreshOffsetFromZone();
    });
  }
}

function schedulePlaceSearch(query) {
  if (placeTimer) clearTimeout(placeTimer);
  placeTimer = setTimeout(() => runPlaceSearch(query), 320);
}

async function runPlaceSearch(query) {
  const list = $("place-suggestions");
  const input = $("field-place");
  if (!list || !input) return;
  if (!canUsePersonalCharts()) {
    hideSuggestions();
    return;
  }

  const q = String(query || "").trim();
  if (q.length < 2) {
    hideSuggestions();
    return;
  }

  if (placeAbort) placeAbort.abort();
  placeAbort = new AbortController();

  try {
    const results = await searchPlaces(q, {
      count: 6,
      signal: placeAbort.signal,
    });
    renderSuggestions(results);
  } catch (err) {
    if (err?.name === "AbortError") return;
    console.warn(err);
    list.innerHTML = `<li class="place-empty">Place lookup unavailable</li>`;
    list.hidden = false;
    input.setAttribute("aria-expanded", "true");
  }
}

function renderSuggestions(results) {
  const list = $("place-suggestions");
  const input = $("field-place");
  if (!list || !input) return;

  list.innerHTML = "";
  if (!results.length) {
    list.innerHTML = `<li class="place-empty">No matches</li>`;
    list.hidden = false;
    input.setAttribute("aria-expanded", "true");
    return;
  }

  for (const place of results) {
    const li = document.createElement("li");
    li.setAttribute("role", "option");
    li.tabIndex = -1;
    li.className = "place-option";
    li.textContent = place.label;
    li.addEventListener("mousedown", (e) => {
      e.preventDefault();
      applyPlace(place);
    });
    li.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        applyPlace(place);
      }
    });
    list.appendChild(li);
  }

  list.hidden = false;
  input.setAttribute("aria-expanded", "true");
}

function hideSuggestions() {
  const list = $("place-suggestions");
  const input = $("field-place");
  if (list) {
    list.hidden = true;
    list.innerHTML = "";
  }
  if (input) input.setAttribute("aria-expanded", "false");
}

/** @param {import('./geocode.js').PlaceResult} place */
function applyPlace(place) {
  suppressPlaceSearch = true;
  selectedPlace = {
    label: place.label,
    latitude: place.latitude,
    longitude: place.longitude,
    timezone: place.timezone || "",
  };

  setVal("field-place", place.label);
  setVal("field-latitude", roundCoord(place.latitude));
  setVal("field-longitude", roundCoord(place.longitude));
  setVal("field-iana-zone", place.timezone || "");

  hideSuggestions();
  updatePlaceResolved();
  refreshOffsetFromZone();
  suppressPlaceSearch = false;
}

function updatePlaceResolved() {
  const el = $("place-resolved");
  if (!el) return;

  if (!selectedPlace || !selectedPlace.label) {
    el.hidden = true;
    el.textContent = "";
    return;
  }

  const lat = fmtCoord(selectedPlace.latitude, "N", "S");
  const lon = fmtCoord(selectedPlace.longitude, "E", "W");
  const zone = selectedPlace.timezone ? ` · ${selectedPlace.timezone}` : "";
  el.hidden = false;
  el.textContent = `✓ ${selectedPlace.label} — ${lat}, ${lon}${zone}`;
}

function refreshOffsetFromZone() {
  const zone = ($("field-iana-zone")?.value || selectedPlace?.timezone || "").trim();
  if (!zone) return;

  let local;
  try {
    local = readLocalDateTime();
  } catch {
    return;
  }

  const resolved = resolveOffset(local, zone);
  if (!resolved.valid) return;

  setVal("field-timezone", trimNumber(resolved.offsetHours));
  return resolved;
}

function readLocalDateTime() {
  const int = (id) => {
    const v = parseInt($(id).value, 10);
    if (Number.isNaN(v)) throw new Error(`Invalid integer for ${id}`);
    return v;
  };
  const hour12 = int("field-hour");
  const ampm = ($("field-ampm")?.value || "AM").toUpperCase();
  return {
    year: int("field-year"),
    month: int("field-month"),
    day: int("field-day"),
    hour: toHour24(hour12, ampm),
    minute: int("field-minute"),
  };
}

/**
 * Convert 12-hour clock + AM/PM to 0–23 hour.
 * @param {number} hour12
 * @param {string} ampm "AM" | "PM"
 */
function toHour24(hour12, ampm) {
  if (!Number.isInteger(hour12) || hour12 < 1 || hour12 > 12) {
    throw new Error("Hour must be 1–12 (use AM/PM for morning or afternoon).");
  }
  const period = String(ampm || "AM").toUpperCase();
  if (period !== "AM" && period !== "PM") {
    throw new Error("Select AM or PM.");
  }
  if (period === "AM") {
    return hour12 === 12 ? 0 : hour12;
  }
  return hour12 === 12 ? 12 : hour12 + 12;
}

/**
 * Convert 0–23 hour to 12-hour clock + AM/PM.
 * @param {number} hour24
 * @returns {{ hour12: number, ampm: 'AM' | 'PM' }}
 */
function fromHour24(hour24) {
  const h = ((Number(hour24) % 24) + 24) % 24;
  if (h === 0) return { hour12: 12, ampm: "AM" };
  if (h === 12) return { hour12: 12, ampm: "PM" };
  if (h < 12) return { hour12: h, ampm: "AM" };
  return { hour12: h - 12, ampm: "PM" };
}

function readBirth() {
  const num = (id) => {
    const v = parseFloat($(id).value);
    if (Number.isNaN(v)) throw new Error(`Invalid number for ${id}`);
    return v;
  };
  const int = (id) => {
    const v = parseInt($(id).value, 10);
    if (Number.isNaN(v)) throw new Error(`Invalid integer for ${id}`);
    return v;
  };

  const hour12 = int("field-hour");
  const ampm = ($("field-ampm")?.value || "AM").toUpperCase();
  const local = {
    year: int("field-year"),
    month: int("field-month"),
    day: int("field-day"),
    hour: toHour24(hour12, ampm),
    minute: int("field-minute"),
  };

  const latitude = num("field-latitude");
  const longitude = num("field-longitude");
  const ianaZone = ($("field-iana-zone")?.value || "").trim();
  let timezone = num("field-timezone");
  let tzMeta = null;

  // Prefer DST-aware offset from IANA zone when available
  if (ianaZone) {
    const resolved = resolveOffset(local, ianaZone);
    if (resolved.valid) {
      timezone = resolved.offsetHours;
      tzMeta = resolved;
      setVal("field-timezone", trimNumber(timezone));
    }
  }

  const placeLabel =
    selectedPlace?.label ||
    ($("field-place")?.value || "").trim() ||
    "";

  if (!placeLabel && (latitude === 0 && longitude === 0)) {
    throw new Error("Choose a birth place (or enter coordinates under Advanced).");
  }

  return {
    name: ($("field-name").value || "Natal chart").trim(),
    placeLabel,
    ...local,
    timezone,
    latitude,
    longitude,
    ianaZone,
    tzMeta,
    houseSystem: "placidus",
  };
}
/**
 * Render the deterministic 7-Year Life Clock card beneath the reading.
 * Uses the birth date only — no chart model, no network, no AI tokens.
 */
function renderLifeClockCard(birth) {
  const container = $("cycle-insight");
  if (!container) return;
  if (!birth || !birth.year || !birth.month || !birth.day) {
    renderCycleInsight(container, null);
    return;
  }
  try {
    const birthDate = new Date(birth.year, birth.month - 1, birth.day);
    const clock = computeLifeClock({
      birthDate,
      referenceDate: new Date(),
      name: birth.name || "",
    });
    renderCycleInsight(container, clock);
  } catch (err) {
    console.error("Life clock render failed", err);
    renderCycleInsight(container, null);
  }
}

function calculateFromForm(opts = {}) {
  const status = $("chart-status");
  const errBox = $("chart-error");
  clearError(errBox);

  const forceDemo = opts.forceDemo === true;
  if (!forceDemo && !canUsePersonalCharts()) {
    showError(
      errBox,
      "Personal charts are locked while the site is under construction. Use Tester unlock or the Donald Trump sample."
    );
    if (status) {
      status.hidden = false;
      status.textContent = "Locked — demo only.";
    }
    return;
  }

  let birth;
  try {
    if (!canUsePersonalCharts()) {
      birth = { ...DEFAULT_BIRTH };
      selectedPlace = {
        label: DEFAULT_BIRTH.placeLabel,
        latitude: DEFAULT_BIRTH.latitude,
        longitude: DEFAULT_BIRTH.longitude,
        timezone: DEFAULT_BIRTH.ianaZone,
      };
    } else {
      birth = readBirth();
    }
  } catch (err) {
    showError(errBox, err.message || String(err));
    return;
  }

  if (status) {
    status.hidden = false;
    status.textContent = "Calculating…";
  }

  requestAnimationFrame(async () => {
    try {
      const model = await runPipeline(birth, {
        chartEl: $("astro-chart"),
        positionsEl: $("positions-table"),
        aspectsEl: $("aspects-table"),
      });

      updateMeta(birth, model);
      updateCaption(birth);
      lastModel = model;
      lastBirth = birth;
      renderLifeClockCard(birth);
      updateInterpretationUi();

      if (status) {
        status.textContent = canUsePersonalCharts()
          ? "Chart ready (Celestine math · AstroChart wheel)."
          : "Sample chart ready (demo only — personal charts locked).";
      }

    } catch (err) {
      console.error(err);
      showError(errBox, err.message || String(err));
      if (status) status.textContent = "Calculation failed.";
    }
  });
}
function updateMeta(birth, model) {
  setText("meta-name", birth.name);
  setText("meta-datetime", formatBirthStamp(birth));

  const coords = `${fmtCoord(birth.latitude, "N", "S")}, ${fmtCoord(
    birth.longitude,
    "E",
    "W"
  )}`;
  const loc = birth.placeLabel ? `${birth.placeLabel} (${coords})` : coords;
  setText("meta-location", loc);

  setText("meta-house-system", model.houseSystemName || model.houseSystem);
}

function updateCaption(birth) {
  const el = $("chart-caption");
  if (!el) return;
  const place = birth.placeLabel ? ` · ${birth.placeLabel}` : "";
  el.textContent = `Figure 1. Natal chart — ${birth.name}${place} — computed live (Celestine) and drawn with AstroChart.`;
}

function formatBirthStamp(b) {
  const mon = String(b.month).padStart(2, "0");
  const day = String(b.day).padStart(2, "0");
  const clock = fromHour24(b.hour);
  const h = String(clock.hour12);
  const mi = String(b.minute).padStart(2, "0");

  let tzLabel = formatUtcOffset(b.timezone);
  if (b.tzMeta?.abbreviation) {
    tzLabel = `${b.tzMeta.abbreviation} (${formatUtcOffset(b.timezone)})`;
  } else if (b.ianaZone) {
    tzLabel = `${b.ianaZone} (${formatUtcOffset(b.timezone)})`;
  }

  return `${b.year}-${mon}-${day} ${h}:${mi} ${clock.ampm} ${tzLabel}`;
}
function fmtCoord(value, pos, neg) {
  const abs = Math.abs(value).toFixed(3);
  return `${abs}°${value >= 0 ? pos : neg}`;
}

function roundCoord(n) {
  return Math.round(Number(n) * 1000) / 1000;
}

function trimNumber(n) {
  const x = Math.round(Number(n) * 1000) / 1000;
  return Object.is(x, -0) ? 0 : x;
}

function setText(id, text) {
  const el = $(id);
  if (el) el.textContent = text;
}

function showError(el, msg) {
  if (!el) return;
  el.hidden = false;
  el.textContent = msg;
}

function clearError(el) {
  if (!el) return;
  el.hidden = true;
  el.textContent = "";
}

/* ------------------------------------------------------------------ */
/* Paywall — purchase & access-code unlock (Phase 3d)                 */
/* ------------------------------------------------------------------ */

/** Small JSON POST helper to the same-origin /api/* backend. */
async function postJson(url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    // Attach the server's message so callers can surface the real reason.
    const err = new Error(data?.message || `Request failed (${res.status})`);
    err.data = data;
    throw err;
  }
  return { ok: res.ok, status: res.status, data };
}

/**
 * JSON POST to the same-origin /api/<name> endpoint (Cloudflare Pages routes
 * functions/api/<name>.js to /api/<name>).
 */
async function postFunction(name, body) {
  return postJson(`/api/${name}`, body);
}

async function createCheckout(readings, email) {
  const res = await postFunction("create-checkout-session", {
    readings: Number(readings) || 1,
    email,
  });
  if (!res.ok || !res.data?.url) {
    throw new Error(res.data?.message || "Could not start checkout. Please try again.");
  }
  return res.data.url;
}

function bindBuyForm() {
  const form = $("buy-form");
  if (!form) return;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const status = $("buy-status");
    const btn = $("btn-buy-readings");
    const readings = document.querySelector('input[name="pack"]:checked')?.value || "1";
    const email = $("buy-email")?.value || "";
    if (!email || !email.includes("@")) {
      if (status) {
        status.hidden = false;
        status.textContent = "Enter a valid email so we can send your access code.";
        status.className = "chart-status is-error";
      }
      return;
    }
    clearError(status);
    if (btn) btn.disabled = true;
    try {
      const url = await createCheckout(readings, email);
      window.location.assign(url);
    } catch (err) {
      showError(status, err.message || String(err));
      status?.classList?.add("is-error");
      status?.classList?.remove("is-ok");
      if (btn) btn.disabled = false;
    }
  });
}

function bindUnlockForm() {
  const form = $("unlock-form");
  if (!form) return;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const status = $("unlock-status");
    const btn = $("btn-unlock-readings");
    const email = $("unlock-email")?.value || "";
    const code = $("unlock-code")?.value || "";
    if (btn) btn.disabled = true;
    clearError(status);
    try {
      const res = await postFunction("access-status", { email, code });
      if (!res.ok || !res.data?.ok) {
        throw new Error(res.data?.message || "That email / access code does not match.");
      }
      setAccessSession({
        email: res.data.email,
        code: res.data.code,
        balance: res.data.balance,
      });
      if (status) {
        status.hidden = false;
        status.textContent = `Unlocked! ${res.data.balance} reading${
          res.data.balance === 1 ? "" : "s"
        } remaining on this code.`;
        status.className = "chart-status is-ok";
      }
      applyAccessState();
      applyPaywallState();
    } catch (err) {
      showError(status, err.message || String(err));
      status?.classList?.add("is-error");
      status?.classList?.remove("is-ok");
    } finally {
      if (btn) btn.disabled = false;
    }
  });
}

function bindPaywall() {
  bindBuyForm();
  bindUnlockForm();
  // "Use a different email or pass": clear this browser's saved access session
  // and reload so the purchase/unlock forms come back for another email.
  $("switch-pass")?.addEventListener("click", () => {
    clearAccessSession();
    window.location.reload();
  });
}

/** Toggle the paywall between "buy" and "you have access" states. */
function applyPaywallState() {
  const paywall = $("paywall");
  if (!paywall) return;
  const balanceBox = $("paywall-balance");
  const buyForm = $("buy-form");
  const unlockForm = $("unlock-form");
  const orDivider = paywall.querySelector(".paywall-or");

  // Testers (friend invites) get the tester tools instead of the paywall.
  const buyer = hasAccessSession();
  const tester = hasValidTesterSession();

  // A buyer whose balance has hit 0 sees the purchase forms again to top up.
  const balance = buyer ? getAccessBalance() : null;
  const exhausted = buyer && balance !== null && balance <= 0;

  // The paywall card itself stays visible so the active-pass banner shows;
  // only the purchase/unlock forms swap out.
  paywall.hidden = tester;
  const showForms = !buyer || exhausted;
  for (const el of [buyForm, unlockForm, orDivider]) {
    if (el) el.style.display = showForms ? "" : "none";
  }

  // The "switch pass" link only makes sense while a pass is active on this
  // browser; once forms are showing it has nothing to clear.
  const switchBtn = $("switch-pass");
  if (switchBtn) switchBtn.hidden = !(buyer && !exhausted);

  if (buyer && balanceBox) {
    balanceBox.hidden = false;
    const code = getAccessCode();
    balanceBox.textContent = exhausted
      ? `All readings used up for ${getAccessEmail()} — buy another pack below (use the same email and access code ${code || "(see below)"} to keep your balance).`
      : `Reading pass active for ${getAccessEmail()}.\nPresent pass: ${code}. ${
          balance === null ? "Unlimited readings." : balance + " reading" + (balance === 1 ? "" : "s") + " remaining."
        }\nSave this code to unlock readings on any other device.`;
    balanceBox.style.whiteSpace = "pre-line";
  }

  // Keep the buyer's own email + code pre-filled in the unlock form so the
  // code is always copyable for use on another browser or device.
  const unlockEmail = $("unlock-email");
  const unlockCode = $("unlock-code");
  if (unlockEmail && !unlockEmail.value) unlockEmail.value = getAccessEmail() || "";
  if (unlockCode && !unlockCode.value) unlockCode.value = getAccessCode() || "";
}

/**
 * If Stripe redirected us back with ?session_id=..., verify the payment and
 * store the buyer's access code, then clean the URL so a reload is safe.
 */
async function handleCheckoutReturn() {
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  const cancelled = params.get("checkout") === "cancelled";
  if (sessionId) {
    const status = $("paywall-balance");
    try {
      const res = await postFunction("verify-session", { sessionId });
      if (res.ok && res.data?.ok && !res.data?.degraded) {
        setAccessSession({
          email: res.data.email,
          code: res.data.code,
          balance: res.data.balance,
        });
        if (status) {
          status.hidden = false;
          status.classList.remove("is-error");
          status.textContent = `Payment received! Access code ${res.data.code} — ${
            res.data.balance
          } reading${res.data.balance === 1 ? "" : "s"} ready. Generating unlocked below.`;
        }
      } else if (res.ok && res.data?.ok && res.data?.degraded) {
        // Payment verified but the credit store is still converging. The code
        // is deterministic and valid — save it so the buyer can read now.
        setAccessSession({
          email: res.data.email,
          code: res.data.code,
          balance: res.data.balance,
        });
        if (status) {
          status.hidden = false;
          status.classList.remove("is-error");
          status.textContent = `Payment received! Access code ${res.data.code} — ${res.data.balance} reading${res.data.balance === 1 ? "" : "s"} ready. Generating unlocked below.`;
        }
      } else {
        if (status) {
          status.hidden = false;
          status.textContent =
            res.data?.message || "We couldn't confirm your payment. Please check your email for your access code.";
        }
      }
    } catch (err) {
      console.error(err);
      if (status) {
        status.hidden = false;
        status.textContent =
          err?.data?.message ||
          "Could not confirm your payment right now. Your access code email should arrive shortly.";
      }
    }
    // Remove the query string so refresh/reload doesn't re-verify.
    try {
      window.history.replaceState({}, "", window.location.pathname);
    } catch {
      /* ignore */
    }
  }
  if (cancelled) {
    const status = $("paywall-balance");
    if (status) {
      status.hidden = false;
      status.textContent = "Checkout cancelled — you have not been charged. You can try again anytime.";
    }
  }
  applyAccessState();
  applyPaywallState();
}
function bindInterpretationTools() {
  // Persist the reader's life-intensity choice so it carries across sessions.
  const INTENSITY_KEY = "ga_life_intensity";
  const checked = document.querySelector(
    'input[name="life-intensity"]:checked'
  );
  if (checked) {
    try {
      const saved = window.localStorage.getItem(INTENSITY_KEY);
      if (
        saved &&
        document.querySelector(`input[name="life-intensity"][value="${saved}"]`)
      ) {
        document
          .querySelector(`input[name="life-intensity"][value="${saved}"]`)
          .checked = true;
      }
    } catch {
      /* storage unavailable — keep the HTML default */
    }
  }
  document.querySelectorAll('input[name="life-intensity"]').forEach((radio) => {
    radio.addEventListener("change", () => {
      const checkedRadio = document.querySelector(
        'input[name="life-intensity"]:checked'
      );
      if (checkedRadio) {
        try {
          window.localStorage.setItem(
            INTENSITY_KEY,
            checkedRadio.value
          );
        } catch {
          /* ignore */
        }
      }
    });
  });
  $("btn-generate-reading")?.addEventListener("click", () => {
    // Once a reading is showing, the button reads "Regenerate Growth Reading";
    // clicking it requests a fresh server generation (bypasses the cached copy).
    const btn = $("btn-generate-reading");
    const forceRefresh =
      typeof btn?.textContent === "string" &&
      btn.textContent.includes("Regenerate");
    void generateReadingFromLastModel("llm", { forceRefresh });
  });
  $("btn-generate-outline")?.addEventListener("click", () => {
    void generateReadingFromLastModel("outline");
  });
  $("btn-show-sample")?.addEventListener("click", () => {
    restoreSampleReading();
  });
  $("btn-print-reading")?.addEventListener("click", () => {
    printReading();
  });
  $("btn-copy-reading")?.addEventListener("click", () => {
    void copyReading();
  });
}

function printReading() {
  const btn = $("btn-print-reading");
  if (btn) btn.disabled = true;
  try {
    window.print();
  } finally {
    if (btn) btn.disabled = false;
  }
}

/** Copy the last generated LLM reading to the clipboard (markdown source). */
async function copyReading() {
  const btn = $("btn-copy-reading");
  if (!lastReadingMarkdown) return;
  const text = `Growth Astrology — Reading for ${lastReadingName}\n\n${lastReadingMarkdown}`;
  let copied = false;
  try {
    await navigator.clipboard.writeText(text);
    copied = true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      copied = true;
    } catch {
      copied = false;
    }
  }
  if (btn) {
    const original = btn.textContent;
    btn.textContent = copied ? "Copied ✓" : "Copy failed";
    btn.disabled = true;
    setTimeout(() => {
      btn.textContent = original;
      btn.disabled = false;
    }, 1600);
  }
}



function updateInterpretationUi() {
  const tools = $("interp-tester-tools");
  const genReading = $("btn-generate-reading");
  const genOutline = $("btn-generate-outline");
  const sampleBtn = $("btn-show-sample");
  const unlocked = canUsePersonalCharts();

  if (tools) tools.hidden = !unlocked;

  // A non-admin tester may exhaust their capped uses; admins are unlimited.
  // Paid access-code users can keep generating while their balance is > 0
  // (the server enforces the actual remaining credits).
  const usesLeft = isAdminSession() ? Infinity : getTesterUsesRemaining();
  const paidUnlocked = !!hasAccessSession();
  const canGen = !!unlocked && !!lastModel && (usesLeft > 0 || paidUnlocked);
  if (genReading) genReading.disabled = !canGen;
  if (genOutline) genOutline.disabled = !canGen;

  if (usesLeft === 0 && unlocked && !isAdminSession() && !paidUnlocked) {
    const errBox = $("interp-error");
    if (errBox) {
      errBox.hidden = false;
      errBox.textContent = `You have reached the limit of ${TESTER_MAX_USES} tester readings. Thank you for testing!`;
    }
  }

  if (sampleBtn) {
    sampleBtn.hidden = !showingDynamic;
  }

  // Print + Copy are only available for a personalized generated LLM reading,
  // never for the static Donald Trump sample or a fact outline.
  const printBtn = $("btn-print-reading");
  if (printBtn) {
    printBtn.hidden = !showingDynamic || showingOutline;
  }
  const copyBtn = $("btn-copy-reading");
  if (copyBtn) {
    copyBtn.hidden = !showingDynamic || showingOutline || !lastReadingMarkdown;
  }

  if (!unlocked && showingDynamic) {
    restoreSampleReading();
  }
}


/**
 * @param {"llm" | "outline"} mode
 * @param {{ forceRefresh?: boolean }} [opts] - forceRefresh bypasses the cached LLM reading.
 */
async function generateReadingFromLastModel(mode, opts = {}) {
  const errBox = $("interp-error");
  const status = $("interp-status");
  clearError(errBox);

  if (!canUsePersonalCharts()) {
    showError(errBox, "Generation is available to unlocked testers only.");
    return;
  }

  let birth;
  let model;
  try {
    birth = readBirth();
  } catch (err) {
    showError(errBox, err.message || String(err));
    return;
  }

  if (status) {
    status.hidden = false;
    status.classList.add("is-loading");
    status.textContent =
      mode === "llm"
        ? "Recalculating chart and requesting AI reading (up to 30s)…"
        : "Recalculating chart and building outline…";
  }

  const genReading = $("btn-generate-reading");
  const genOutline = $("btn-generate-outline");
  if (genReading) genReading.disabled = true;
  if (genOutline) genOutline.disabled = true;

  try {
    model = await runPipeline(birth, {
      chartEl: $("astro-chart"),
      positionsEl: $("positions-table"),
      aspectsEl: $("aspects-table"),
    });
    updateMeta(birth, model);
    updateCaption(birth);
    lastModel = model;
    lastBirth = birth;
    renderLifeClockCard(birth);

    const payload = buildInterpretationPayload(model, {
      displayName: birth.name || null,
      placeLabel: birth.placeLabel || null,
    });

    const sampleEl = $("sample-reading");
    const dynamicEl = $("dynamic-reading");
    let markdownChars = 0;
    let fromCache = false;

    if (mode === "llm") {
      // Resolve the active auth: paid access session wins; otherwise tester.
      const access = buildAccessAuth();
      let auth;
      let isAccess = false;
      if (access) {
        auth = access;
        isAccess = true;
      } else {
        const email = getTesterSessionEmail();
        const code = getTesterSessionCode();
        if (!email || !code) {
          throw new Error(
            "No active reading pass. Unlock with your email + access code, or a tester invite, then retry."
          );
        }
        auth = { type: "tester", email, code };
      }
      showDynamicReading(sampleEl, dynamicEl);
      const body = document.createElement("div");
      body.className = "reading-body dynamic-reading-body";
      dynamicEl.replaceChildren(body);
      showingDynamic = true;
      showingOutline = false;

      // Caching (hybrid consistency): the reading text is keyed by the stable
      // chart payload hash PLUS the selected life-intensity, so switching the
      // intensity dial requests a fresh, differently-calibrated reading. A cached
      // reading is instant, identical, and costs no tokens. forceRefresh (the
      // "Regenerate" button) always calls the server.
      const lifeIntensity =
        document.querySelector('input[name="life-intensity"]:checked')?.value ||
        "dramatic";
      const cacheKey = `ga_reading_${lifeIntensity}_${hashPayload(payload)}`;
      let md = null;
      if (!opts.forceRefresh) {
        try {
          const cached = window.localStorage.getItem(cacheKey);
          if (typeof cached === "string" && cached.length > 0) {
            md = cached;
            fromCache = true;
          }
        } catch {
          /* storage unavailable — fall through to a live request */
        }
      }

      if (md === null) {
        md = await fetchLlmReading(payload, auth, { lifeIntensity });
        markdownChars = md.length;
        // A successfully generated reading cost a credit on a paid pass.
        if (isAccess) decrementAccessBalance();
        applyPaywallState();
        try {
          window.localStorage.setItem(cacheKey, md);
        } catch {
          /* storage full/unavailable — the reading still renders fine */
        }
      } else {
        markdownChars = md.length;
      }

      renderStreamingText(body, md);
      lastReadingMarkdown = md;
      lastReadingName = birth.name || "this chart";
      if (genReading) genReading.textContent = "Regenerate Growth Reading";
    } else {
      const reading = buildOutlineReading(payload);
      showDynamicReading(sampleEl, dynamicEl);
      renderReadingDocument(dynamicEl, reading);
      showingDynamic = true;
      showingOutline = true;
      lastReadingMarkdown = null;
      lastReadingName = null;
      // An outline is not cached like the LLM reading; reset the label so the
      // next click asks the server normally rather than forcing a refresh.
      if (genReading) genReading.textContent = "Generate Growth Reading";
    }

    const plutoH = payload.derived?.pluto?.body?.house;
    const pppH = payload.derived?.pluto?.ppp?.house;
    const plutoSign = payload.derived?.pluto?.body?.signName;
    const pppSign = payload.derived?.pluto?.ppp?.signName;

    const lead = $("interp-lead");
    if (lead) {
      if (mode === "llm") {
        lead.textContent =
          "AI reading for " +
          (birth.name || "this chart") +
          " (Phase 2b). Pluto " +
          plutoSign +
          " house " +
          plutoH +
          "; PPP " +
          pppSign +
          " house " +
          pppH +
          ". Generated via server — static sample is hidden.";
      } else {
        lead.textContent =
          "Fact outline for " +
          (birth.name || "this chart") +
          ". Pluto " +
          plutoSign +
          " H" +
          plutoH +
          "; PPP " +
          pppSign +
          " H" +
          pppH +
          ".";
      }
    }

    if (status) {
      const kind = mode === "llm" ? "AI reading" : "Outline";
      const generated = fromCache ? "loaded from cache" : "generated";
      const detail =
        mode === "llm"
          ? `${markdownChars} char markdown · ${generated}`
          : `${reading.sections.length} sections · ${reading.source}`;
      status.textContent =
        kind +
        " ready · Pluto " +
        plutoSign +
        " H" +
        plutoH +
        " · PPP " +
        pppSign +
        " H" +
        pppH +
        " · " +
        detail;
    }

    // Count a tester's use (admins are never counted / limited).
    recordTesterUse();

    updateInterpretationUi();
    dynamicEl?.scrollIntoView({ behavior: "smooth", block: "start" });
  } catch (err) {
    console.error(err);
    showError(errBox, err.message || String(err));
    if (status) {
      status.textContent =
        err.code === "timeout"
          ? "Timed out — previous content kept."
          : "Generation failed.";
    }
  } finally {
    if (status) status.classList.remove("is-loading");
    updateInterpretationUi();
  }
}

function restoreSampleReading() {
  showSampleReading($("sample-reading"), $("dynamic-reading"));
  renderLifeClockCard({ ...DEFAULT_BIRTH });
  showingDynamic = false;
  showingOutline = false;
  const genReading = $("btn-generate-reading");
  if (genReading) genReading.textContent = "Generate Growth Reading";
  clearError($("interp-error"));
  const status = $("interp-status");
  if (status) {
    status.hidden = true;
    status.textContent = "";
  }
  const lead = $("interp-lead");
  if (lead) {
    lead.textContent =
      "A reading for the featured chart above for Donald Trump, produced by the trained AI agent to match the Growth Astrology Reading Blueprint. Sign and house names match the live calculation (Placidus). If you calculate a different birth, the wheel and tables update; this essay stays the sample reading until a dynamic reading is generated from new birth data.";
  }
  updateInterpretationUi();
}


if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

